/**
 * WebSocket 封装库
 * 支持自动重连、心跳检测、消息队列等功能
 */

import { getCookies } from "@/utils/utils";
import baseUrl from "@/fetch/url";

class WebSocketClient {
  constructor(options = {}) {
    this.url = options.url || this.getDefaultUrl();
    this.socket = null;
    this.isConnected = false;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = options.maxReconnectAttempts || 5;
    this.reconnectInterval = options.reconnectInterval || 3000;
    this.heartbeatInterval = options.heartbeatInterval || 30000;
    this.heartbeatTimer = null;
    this.reconnectTimer = null;
    this.messageQueue = [];

    // 事件回调
    this.onOpen = options.onOpen || (() => {});
    this.onMessage = options.onMessage || (() => {});
    this.onClose = options.onClose || (() => {});
    this.onError = options.onError || (() => {});
    this.onReconnect = options.onReconnect || (() => {});
  }

  // 获取默认的 WebSocket URL
  getDefaultUrl() {
    // 将 http/https 转换为 ws/wss
    let wsUrl = baseUrl.url || '';
    if (wsUrl.startsWith('//')) {
      wsUrl = window.location.protocol === 'https:' ? 'wss:' + wsUrl : 'ws:' + wsUrl;
    } else if (wsUrl.startsWith('http://')) {
      wsUrl = wsUrl.replace('http://', 'ws://');
    } else if (wsUrl.startsWith('https://')) {
      wsUrl = wsUrl.replace('https://', 'wss://');
    }
    return wsUrl + 'ws/chat';
  }

  // 连接 WebSocket
  connect(url, chatToken) {
    if (url) {
      this.url = url;
    }
    if (chatToken) {
      this.chatToken = chatToken;
    }

    if (this.socket && this.socket.readyState === WebSocket.OPEN) {
      console.log('WebSocket 已经连接');
      return;
    }

    try {
      // 优先使用 chatToken，否则使用 cookie 中的 token
      const token = this.chatToken || getCookies('token') || '';
      const connectUrl = this.url + (this.url.includes('?') ? '&' : '?') + 'token=' + token;

      this.socket = new WebSocket(connectUrl);
      this.initEventHandlers();
    } catch (error) {
      console.error('WebSocket 连接失败:', error);
      this.onError(error);
      this.scheduleReconnect();
    }
  }

  // 初始化事件处理器
  initEventHandlers() {
    this.socket.onopen = (event) => {
      console.log('WebSocket 连接成功');
      this.isConnected = true;
      this.reconnectAttempts = 0;
      this.startHeartbeat();
      this.flushMessageQueue();
      this.onOpen(event);
    };

    this.socket.onmessage = (event) => {
      let data = event.data;
      try {
        data = JSON.parse(event.data);
      } catch (e) {
        // 如果不是 JSON 格式，保持原样
      }

      // 处理心跳响应
      if (data.type === 'pong' || data === 'pong') {
        return;
      }

      this.onMessage(data, event);
    };

    this.socket.onclose = (event) => {
      console.log('WebSocket 连接关闭', event);
      this.isConnected = false;
      this.stopHeartbeat();
      this.onClose(event);

      // 非正常关闭时尝试重连
      if (!event.wasClean) {
        this.scheduleReconnect();
      }
    };

    this.socket.onerror = (error) => {
      console.error('WebSocket 错误:', error);
      this.onError(error);
    };
  }

  // 发送消息
  send(data) {
    const message = typeof data === 'string' ? data : JSON.stringify(data);

    if (this.isConnected && this.socket.readyState === WebSocket.OPEN) {
      this.socket.send(message);
      return true;
    } else {
      // 连接未建立，加入消息队列
      this.messageQueue.push(message);
      console.log('WebSocket 未连接，消息已加入队列');
      return false;
    }
  }

  // 清空消息队列
  flushMessageQueue() {
    while (this.messageQueue.length > 0) {
      const message = this.messageQueue.shift();
      this.socket.send(message);
    }
  }

  // 开始心跳检测
  startHeartbeat() {
    this.stopHeartbeat();
    this.heartbeatTimer = setInterval(() => {
      if (this.isConnected) {
        this.send({ type: 'ping' });
      }
    }, this.heartbeatInterval);
  }

  // 停止心跳检测
  stopHeartbeat() {
    if (this.heartbeatTimer) {
      clearInterval(this.heartbeatTimer);
      this.heartbeatTimer = null;
    }
  }

  // 安排重连
  scheduleReconnect() {
    if (this.reconnectAttempts >= this.maxReconnectAttempts) {
      console.log('WebSocket 重连次数已达上限');
      return;
    }

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
    }

    this.reconnectTimer = setTimeout(() => {
      this.reconnectAttempts++;
      console.log(`WebSocket 尝试第 ${this.reconnectAttempts} 次重连...`);
      this.onReconnect(this.reconnectAttempts);
      this.connect(this.url, this.chatToken);
    }, this.reconnectInterval);
  }

  // 断开连接
  disconnect() {
    this.stopHeartbeat();

    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    if (this.socket) {
      this.socket.close(1000, '主动断开连接');
      this.socket = null;
    }

    this.isConnected = false;
    this.reconnectAttempts = this.maxReconnectAttempts; // 防止自动重连
  }

  // 获取连接状态
  getState() {
    if (!this.socket) return 'CLOSED';
    const states = ['CONNECTING', 'OPEN', 'CLOSING', 'CLOSED'];
    return states[this.socket.readyState];
  }
}

// 创建单例实例
let wsInstance = null;

export const createWebSocket = (options) => {
  if (!wsInstance) {
    wsInstance = new WebSocketClient(options);
  }
  return wsInstance;
};

export const getWebSocket = () => {
  return wsInstance;
};

export const destroyWebSocket = () => {
  if (wsInstance) {
    wsInstance.disconnect();
    wsInstance = null;
  }
};

export default WebSocketClient;



// WEBPACK FOOTER //
// ./src/fetch/websocket.js